import keyBy from 'lodash/keyBy';

import { TFunction } from '@lyearn/i18n';
import { getMtfPairingsInput } from '@/modules/corporate/modules/questions/fields/match-the-following/utils';
import { getInitialQuestionState } from '@/modules/corporate/modules/quiz/views/QuizEditor/utils/questions';
import { getUpdatedSubWorkspaceIds } from '@/modules/corporate/utils/subWorkspaces';
import { getCustomRatingLabels } from '@/modules/user-survey/questions/utils';
import {
  FtbComponent,
  ImageHotspotComponent,
  OptionInput,
  QuestionApplicableOnEnum,
  QuestionInput,
  QuestionSubTypeEnum,
  QuestionTypeEnum,
  QuizQuestionInputType,
  UpdateQuestionEntity,
  UpdateQuizInput,
} from '@/types/schema';

import { QuizEditorDetailsFragment } from '../../graphql/fragments/generated/QuizEditorContent';
import { QuizEditorQuestionFragment } from '../../graphql/fragments/generated/QuizEditorQuestion';

type AdaptToUpdateQuestionEntityParams = {
  question: UpdateQuestionEntity;
  values: any;
  t: TFunction;
  applicableOn: QuestionApplicableOnEnum;
  currentSubWorkspace: string | null;
};

export const adaptToQuestionInput = (question: UpdateQuestionEntity): QuestionInput => {
  const {
    id,
    isActive,
    associations,
    attachments,
    options,
    skillIds,
    ftbComponents,
    globalDistractors,
    themeIds,
    imageHotspotComponents,
    ...rest
  } = question;

  return {
    associations: question.associations?.set,
    attachments: question.attachments?.set,
    options: question.options?.set,
    skillIds: question.skillIds?.set,
    ftbComponents: ftbComponents?.set,
    imageHotspotComponents: imageHotspotComponents?.set.map(({ id, ...rest }) => rest),
    globalDistractors: globalDistractors?.set,
    themeIds: themeIds?.set,
    ...rest,
  };
};

export const adaptToUpdateQuestionEntity = ({
  question,
  values,
  t,
  applicableOn,
  currentSubWorkspace,
}: AdaptToUpdateQuestionEntityParams): UpdateQuestionEntity => {
  const { multiSelect, weightedOption, mtfComponent, ftbComponents, ...rest } = values;
  const subType = multiSelect
    ? QuestionSubTypeEnum.MultiSelect
    : weightedOption
    ? QuestionSubTypeEnum.WeightedOption
    : QuestionSubTypeEnum.SingleSelect;
  const newQuestionType = values.questionType;
  const oldQuestionType = question.questionType;

  // If the question type hasn't changed, skip adapt changes
  if (newQuestionType === oldQuestionType) {
    // to be changed later along with merging single and multiple choice into one
    if (question.subType === QuestionSubTypeEnum.MultiSelect && !multiSelect) {
      const { options, ...restQuestion } = question;
      return {
        ...restQuestion,
        options: {
          set: options?.set.map((option) => ({ ...option, optionEvaluation: false })),
        },
        ...rest,
        subType,
      };
    }

    // Transitioning FROM WeightedOption to other subTypes: clear weights and reset optionEvaluation
    if (question.subType === QuestionSubTypeEnum.WeightedOption && !weightedOption) {
      const { options, ...restQuestion } = question;
      return {
        ...restQuestion,
        options: {
          set: options?.set.map((option) => ({
            ...option,
            optionEvaluation: false,
            optionRelativeScore: null,
          })),
        },
        ...rest,
        subType,
      };
    }

    // Entering or staying in WeightedOption: correctness is derived from weight, not manually set
    if (weightedOption) {
      const { options, ...restQuestion } = question;
      return {
        ...restQuestion,
        options: {
          set: options?.set.map((option) => {
            const optionRelativeScore = option.optionRelativeScore ?? 0;
            return {
              ...option,
              optionEvaluation: optionRelativeScore > 0,
              optionRelativeScore,
              attachments: option.attachments ?? [],
            };
          }),
        },
        ...rest,
        subType,
      };
    }

    if (question.questionType === QuestionTypeEnum.Likert) {
      return {
        ...question,
        ...rest,
        customRatingComponent:
          question.subType === QuestionSubTypeEnum.CustomRating
            ? {
                ratingLabels: getCustomRatingLabels(values),
              }
            : undefined,
      };
    }

    return { ...question, ...rest, subType };
  }

  const adaptedObject = { ...question, ...rest, subType };

  if (oldQuestionType === QuestionTypeEnum.ImageHotspot) {
    adaptedObject.attachments = null;
    adaptedObject.imageHotspotComponents = null;
  }

  if (values.questionType === QuestionTypeEnum.MatchTheFollowing) {
    adaptedObject.ftbComponents = null;
    adaptedObject.imageHotspotComponents = null;
  } else if (values.questionType === QuestionTypeEnum.FillInTheBlanks) {
    adaptedObject.mtfComponent = null;
    adaptedObject.imageHotspotComponents = null;
  } else if (values.questionType === QuestionTypeEnum.ImageHotspot) {
    adaptedObject.ftbComponents = null;
    adaptedObject.mtfComponent = null;
    adaptedObject.imageHotspotComponents = null;
  } else if (applicableOn === QuestionApplicableOnEnum.Quiz) {
    const initialDefaults = getInitialQuestionState(
      newQuestionType,
      t,
      applicableOn,
      currentSubWorkspace,
    )!;
    const initialOptions = initialDefaults.options as OptionInput[];

    // common changes/resets across all question types
    adaptedObject.options = { set: [...initialOptions] };
    adaptedObject.ftbComponents = null;
    adaptedObject.mtfComponent = null;
  }

  // specific to question types
  if (newQuestionType === QuestionTypeEnum.TrueOrFalse) {
    adaptedObject.settings = { ...values.settings, shuffleOptions: false };
  }

  return adaptedObject;
};

export const getQuestionsSetInput = (
  questions?: QuizEditorQuestionFragment[],
): QuizQuestionInputType[] => questions?.map((question) => ({ questionId: question.id })) ?? [];

export const adaptQuiz = (
  quiz: QuizEditorDetailsFragment,
  currentSubWorkspace?: string | null,
): UpdateQuizInput => {
  const questionsSetInput = getQuestionsSetInput(quiz.questions);
  const updatedSubWorkspaceIds = getUpdatedSubWorkspaceIds(
    quiz?.subWorkspaces,
    currentSubWorkspace,
  );
  const skillIds = quiz.skills?.map((skill) => skill.id) ?? [];

  return {
    id: quiz.id,
    name: quiz.name,
    questions: { set: questionsSetInput },
    cover: quiz.cover,
    description: quiz.description,
    objective: quiz.objective,
    skillIds: { set: skillIds },
    certificateSettings: quiz.certificateSettings,
    subWorkspaceIds: updatedSubWorkspaceIds,
    quizSections: quiz.quizSections?.map((s) => ({
      _id: s._id,
      name: s.name,
      order: s.order,
      description: s.description,
      randomizationConfig: s.randomizationConfig
        ? {
            questionGenerationType: s.randomizationConfig.questionGenerationType,
            questionGenerationConfig: s.randomizationConfig.questionGenerationConfig,
          }
        : undefined,
    })),
  };
};

export const adaptQuestionInput = (question: QuizEditorQuestionFragment): UpdateQuestionEntity => {
  const questionOptions = question.options ?? [];
  const correctOptionsById = keyBy(question.correctOptions, 'optionId');
  const options = questionOptions.map((option) => ({
    ...option,
    optionEvaluation: Boolean(correctOptionsById[option.optionId]),
  }));

  return {
    id: question.id,
    description: question.description,
    questionText: question.questionText,
    questionTextType: question.questionTextType,
    questionType: question.questionType,
    correctAnswerFeedback: question.correctAnswerFeedback,
    incorrectAnswerFeedback: question.incorrectAnswerFeedback,
    options: { set: options },
    ftbComponents: {
      set: getFTbComponentsWithAnswers(question.ftbComponents, question.correctFTBAnswers),
    },
    imageHotspotComponents: {
      set: getImageHotspotComponentsWithAnswers(
        question.imageHotspotComponents,
        question.correctImageHotspotAnswers,
      ),
    },
    mtfComponent: {
      leftElements: question.mtfComponent?.leftElements || [],
      rightElements: question.mtfComponent?.rightElements || [],
      pairings: getMtfPairingsInput(question.mtfComponent?.leftElements.length || 0),
    },
    settings: question.settings,
    attachments: { set: question.attachments ?? [] },
    difficulty: question.difficulty,
    skillIds: { set: question.skillIds ?? [] },
    subType: question.subType,
    globalDistractors: { set: question.globalDistractors || [] },
    isActive: question.isActive,
    customRatingComponent: question.customRatingComponent,
    //@ts-ignore,
    // TODO: create some common fragment
    themeIds: { set: question.themeIds || [] },
    subWorkspaceIds: { set: question.subWorkspaces || [] },
  };
};

export const getFTbComponentsWithAnswers = (
  ftbComponents: FtbComponent[],
  correctFtbAnswers: FtbComponent[],
) => {
  const ftbIdToTextMap = correctFtbAnswers.reduce(
    (acc, answer) => {
      acc[answer.id] = answer.text || '';
      return acc;
    },
    {} as { [key: string]: string },
  );

  return ftbComponents.map((component) => {
    if (ftbIdToTextMap[component.id]) {
      return { ...component, text: ftbIdToTextMap[component.id] };
    }

    return component;
  });
};

export const getImageHotspotComponentsWithAnswers = (
  imageHotspotComponents: ImageHotspotComponent[],
  correctImageHotspotAnswers: ImageHotspotComponent[],
) => {
  const imageHotspotIdToTextMap = correctImageHotspotAnswers.reduce(
    (acc, answer) => {
      acc[answer.id] = answer.text || '';
      return acc;
    },
    {} as { [key: string]: string },
  );

  return imageHotspotComponents.map((component) => {
    if (imageHotspotIdToTextMap[component.id]) {
      return { ...component, text: imageHotspotIdToTextMap[component.id] };
    }

    return component;
  });
};
